/**
 * <brand-spotlight> - a rotating brand hero.
 *
 * Crossfades between brand "slides" rendered by sections/brand-spotlight.liquid. Pure progressive
 * enhancement: with JS off, the first slide is already visible and every "Shop <brand>" link is a
 * normal anchor, so the hero still works (the controls are hidden until this element upgrades, via
 * the `:not(:defined)` rule in the section CSS).
 *
 * Behaviour:
 *  - Auto-advances every `data-interval` ms when `data-autoplay` is set (and motion is allowed).
 *  - Prev / next arrows, dot indicators, and an optional pause/play toggle.
 *  - Suspends auto-advance on hover and keyboard focus, and while the tab is hidden; the toggle is a
 *    sticky user pause that survives those.
 *  - Left / right arrow keys move between slides when a control is focused. Touch swipe on the hero.
 *  - Honors prefers-reduced-motion: never auto-advances, and the CSS drops the crossfade so manual
 *    moves are instant.
 *
 * Inactive slides get `inert` + `aria-hidden` so their links stay out of the tab order and a11y tree.
 */

const SWIPE_THRESHOLD = 40;

class BrandSpotlight extends HTMLElement {
  /** @type {HTMLElement[]} */ #panels = [];
  /** @type {HTMLElement[]} */ #media = [];
  /** @type {HTMLElement[]} */ #dots = [];
  /** @type {HTMLElement | null} */ #live = null;
  /** @type {HTMLButtonElement | null} */ #toggle = null;

  #index = 0;
  #count = 0;
  #interval = 6000;
  /** @type {ReturnType<typeof setInterval> | null} */ #timer = null;

  #autoplay = false;
  #userPaused = false;
  #suspended = false;

  #controller = new AbortController();
  /** @type {number | null} */ #touchX = null;

  connectedCallback() {
    this.#panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-bs-slide]')]);
    this.#media = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-bs-media]')]);
    this.#dots = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-bs-dot]')]);
    this.#live = this.querySelector('[data-bs-live]');
    this.#toggle = this.querySelector('[data-bs-toggle]');
    this.#count = this.#panels.length;

    // One slide (or none) is a static hero - nothing to rotate or control.
    if (this.#count <= 1) return;

    this.#interval = Number.parseInt(this.getAttribute('data-interval') || '6000', 10);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#autoplay = this.hasAttribute('data-autoplay') && !reduce;

    const { signal } = this.#controller;

    this.querySelector('[data-bs-prev]')?.addEventListener('click', () => this.#step(-1), { signal });
    this.querySelector('[data-bs-next]')?.addEventListener('click', () => this.#step(1), { signal });

    for (const dot of this.#dots) {
      dot.addEventListener('click', () => this.#go(Number(dot.dataset.index) || 0, true), { signal });
    }

    if (this.#toggle) {
      if (this.#autoplay) {
        this.#toggle.addEventListener('click', () => this.#toggleUserPause(), { signal });
      } else {
        // A pause control is meaningless when nothing auto-advances (e.g. reduced motion).
        this.#toggle.hidden = true;
      }
    }

    this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('touchstart', this.#onTouchStart, { signal, passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { signal, passive: true });
    document.addEventListener('visibilitychange', this.#onVisibility, { signal });

    this.#go(0);
    this.#updateTimer();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#stopTimer();
  }

  /** Move by a relative number of slides (wraps). Restarts the timer so the new slide gets a full dwell. */
  #step(direction) {
    this.#go(this.#index + direction, true);
  }

  /**
   * @param {number} target - Index to show (wraps out of range).
   * @param {boolean} [userInitiated] - Reset the auto-advance timer after a manual move.
   */
  #go(target, userInitiated = false) {
    const next = ((target % this.#count) + this.#count) % this.#count;
    this.#index = next;

    this.#panels.forEach((panel, i) => {
      const active = i === next;
      panel.classList.toggle('is-active', active);
      panel.toggleAttribute('inert', !active);
      // aria-hidden must be the literal "true"/absent - toggleAttribute would set aria-hidden="" (false).
      if (active) panel.removeAttribute('aria-hidden');
      else panel.setAttribute('aria-hidden', 'true');
    });

    this.#media.forEach((media, i) => {
      const active = i === next;
      media.classList.toggle('is-active', active);
      if (active) media.removeAttribute('aria-hidden');
      else media.setAttribute('aria-hidden', 'true');
    });

    this.#dots.forEach((dot, i) => {
      const active = i === next;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    // Announce only on user navigation - auto-advance would otherwise nag screen readers every cycle.
    // Reuses the active slide's localized "Slide X of N" label.
    if (userInitiated && this.#live) {
      this.#live.textContent = this.#panels[next]?.getAttribute('aria-label') || '';
    }

    if (userInitiated) this.#updateTimer();
  }

  #toggleUserPause() {
    this.#userPaused = !this.#userPaused;
    if (this.#toggle) {
      const label = this.#userPaused ? this.#toggle.dataset.labelPlay : this.#toggle.dataset.labelPause;
      if (label) this.#toggle.setAttribute('aria-label', label);
    }
    this.#updateTimer();
  }

  #updateTimer() {
    this.#stopTimer();
    this.toggleAttribute('paused', this.#autoplay && this.#userPaused);

    const running = this.#autoplay && !this.#userPaused && !this.#suspended && !document.hidden;
    if (running) {
      this.#timer = setInterval(() => this.#go(this.#index + 1), this.#interval);
    }
  }

  #stopTimer() {
    if (this.#timer != null) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #onPointerEnter = () => {
    this.#suspended = true;
    this.#updateTimer();
  };

  #onPointerLeave = () => {
    this.#suspended = false;
    this.#updateTimer();
  };

  #onFocusIn = () => {
    this.#suspended = true;
    this.#updateTimer();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#suspended = false;
    this.#updateTimer();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('[data-bs-prev],[data-bs-next],[data-bs-dot],[data-bs-toggle]')) return;
    event.preventDefault();
    this.#step(event.key === 'ArrowLeft' ? -1 : 1);
  };

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    this.#touchX = event.touches[0]?.clientX ?? null;
  };

  /** @param {TouchEvent} event */
  #onTouchEnd = (event) => {
    if (this.#touchX == null) return;
    const delta = (event.changedTouches[0]?.clientX ?? this.#touchX) - this.#touchX;
    this.#touchX = null;
    if (Math.abs(delta) > SWIPE_THRESHOLD) this.#step(delta < 0 ? 1 : -1);
  };

  #onVisibility = () => this.#updateTimer();
}

if (!customElements.get('brand-spotlight')) {
  customElements.define('brand-spotlight', BrandSpotlight);
}
